import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { useI18n } from '../../contexts/I18nContext';
import { Search, Car, Cpu, Fuel, Gauge, Wrench, Filter } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import api from '../../services/api';

const ToolsPage = () => {
  const { t } = useI18n();
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [versions, setVersions] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [fuelFilter, setFuelFilter] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/vehicles/brands').then(res => setBrands(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      setSelectedModel('');
      setVersions([]);
      api.get(`/vehicles/models/${encodeURIComponent(selectedBrand)}`).then(res => setModels(res.data)).catch(() => {});
    }
  }, [selectedBrand]);

  useEffect(() => {
    if (selectedBrand && selectedModel) {
      setLoading(true);
      api.get(`/vehicles/versions/${encodeURIComponent(selectedBrand)}/${encodeURIComponent(selectedModel)}`)
        .then(res => setVersions(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [selectedBrand, selectedModel]);

  const handleSearch = useCallback(async () => {
    if (!search.trim()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: search, limit: '50' });
      if (fuelFilter) params.set('fuel', fuelFilter);
      const res = await api.get(`/vehicles/search?${params.toString()}`);
      setSearchResults(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [search, fuelFilter]);

  const displayData = searchResults.length > 0 ? searchResults : versions;
  const fuelTypes = ['Diesel', 'Petrol', 'Hybrid', 'Electric', 'Ethanol'];

  const getFuelColor = (fuel) => {
    const colors = { Diesel: 'text-blue-400 bg-blue-500/10', Petrol: 'text-orange-400 bg-orange-500/10', Hybrid: 'text-emerald-400 bg-emerald-500/10', Electric: 'text-cyan-400 bg-cyan-500/10' };
    return colors[fuel] || 'text-slate-400 bg-slate-500/10';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="tools-page">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">{t('nav.tools')}</h1>
          <p className="text-sm text-slate-500 mt-1">Fahrzeugdatenbank mit über 17.000 kompatiblen Fahrzeugen</p>
        </div>

        {/* Search Bar */}
        <div className="glass-card p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Marke, Modell, Motor oder ECU suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="input-field pl-9"
                data-testid="tools-search"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={fuelFilter}
                onChange={(e) => setFuelFilter(e.target.value)}
                className="input-field text-sm px-3 py-2 bg-[#1A1D23] border border-white/10 rounded-lg text-white"
                data-testid="fuel-filter"
              >
                <option value="">Alle Kraftstoffe</option>
                {fuelTypes.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <Button onClick={handleSearch} className="bg-[#EC2229] hover:bg-[#D01F25] text-white" data-testid="tools-search-btn">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Brand / Model Selector */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="glass-card p-4">
            <label className="label-text mb-2 block">Marke</label>
            <select
              value={selectedBrand}
              onChange={(e) => { setSelectedBrand(e.target.value); setSearchResults([]); }}
              className="input-field w-full text-sm bg-[#1A1D23] border border-white/10 rounded-lg text-white"
              data-testid="brand-select"
            >
              <option value="">Marke wählen ({brands.length})</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="glass-card p-4">
            <label className="label-text mb-2 block">Modell</label>
            <select
              value={selectedModel}
              onChange={(e) => { setSelectedModel(e.target.value); setSearchResults([]); }}
              className="input-field w-full text-sm bg-[#1A1D23] border border-white/10 rounded-lg text-white"
              disabled={!selectedBrand}
              data-testid="model-select"
            >
              <option value="">Modell wählen ({models.length})</option>
              {models.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-20 bg-white/5 animate-pulse rounded-xl" />)}
          </div>
        ) : displayData.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-500">{displayData.length} Fahrzeuge gefunden</p>
            {displayData.map((v, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-3 items-center p-4 rounded-xl bg-[#141418] border border-white/[0.04] hover:border-[#EC2229]/20 transition-all" data-testid={`vehicle-row-${idx}`}>
                <div className="col-span-3 sm:col-span-2">
                  <p className="text-white font-medium text-sm">{v.brand}</p>
                  <p className="text-white/50 text-xs">{v.model}</p>
                </div>
                <div className="col-span-3 sm:col-span-3">
                  <p className="text-white/70 text-xs truncate">{v.version || v.engine || '-'}</p>
                  <p className="text-white/40 text-xs">{v.engine_type || ''}</p>
                </div>
                <div className="col-span-2 sm:col-span-2">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${getFuelColor(v.fuel)}`}>
                    <Fuel className="w-3 h-3" />
                    {v.fuel || '-'}
                  </span>
                </div>
                <div className="col-span-2 sm:col-span-2 text-center">
                  {v.power_ps > 0 && (
                    <div className="flex items-center gap-1 justify-center">
                      <Gauge className="w-3 h-3 text-[#EC2229]" />
                      <span className="text-sm font-semibold text-white">{v.power_ps} PS</span>
                    </div>
                  )}
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <p className="text-xs text-white/50 flex items-center gap-1">
                    <Cpu className="w-3 h-3 text-[#EC2229]" />
                    {v.ecu_maker} {v.ecu_model || ''}
                  </p>
                  <p className="text-xs text-white/30 flex items-center gap-1 mt-0.5">
                    <Wrench className="w-3 h-3" />
                    {v.connection_mode || '-'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (selectedBrand || search) && !loading ? (
          <div className="glass-card p-12 text-center">
            <Car className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-white font-medium">Keine Ergebnisse</p>
            <p className="text-sm text-slate-500 mt-1">Versuchen Sie eine andere Suche oder wählen Sie ein Modell.</p>
          </div>
        ) : (
          <div className="glass-card p-12 text-center">
            <Car className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-white font-medium">Fahrzeugdatenbank</p>
            <p className="text-sm text-slate-500 mt-1">Wählen Sie eine Marke und ein Modell oder nutzen Sie die Suche.</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ToolsPage;
